'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { getLeaveMessage } from './NavigationGuard'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const pathname = usePathname()
  const router = useRouter()

  if (pathname.startsWith('/marketup')) return null;

  const hiddenButtonRoutes = ['/cadastro', '/resumo', '/assinatura/checkout'];
  const shouldHideButtons = hiddenButtonRoutes.some(route => pathname === route || pathname.startsWith(`${route}/`));

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  // Lógica inteligente para "Como Funciona"
  const handleComoFunciona = (e: React.MouseEvent) => {
    e.preventDefault()

    // Verificação programática de saída para rotas protegidas (já que este é um <button> e não um <a>)
    const protectedRoutes = ['/cadastro', '/resumo', '/assinatura/checkout'];
    const isProtected = protectedRoutes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );
    if (isProtected) {
      const confirmLeave = window.confirm(getLeaveMessage(pathname));
      if (!confirmLeave) return; // Se cancelar, aborta a navegação
    }

    // Páginas onde deve fazer scroll para a seção
    const pagesWithSection = ['/', '/assinatura', '/planos']

    if (pagesWithSection.includes(pathname)) {
      // Está em uma página que tem a seção - fazer scroll
      const element = document.getElementById('comoFunciona')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      // Não está em uma página com a seção - ir para home
      router.push('/#comoFunciona')
    }

    closeMenu() // Fechar menu mobile se estiver aberto
  }



  const handleAgendarExames = () => {
    if (pathname === '/') {
      // Na página inicial, rola para o formulário
      const element = document.getElementById('formularioAgendamento')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      // Em outras páginas, vai para a home e rola para o formulário
      router.push('/#formularioAgendamento')
    }
  }

  return (
    <header className="sticky top-0 bg-white shadow-sm z-50">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link href="/" className="flex items-center" onClick={closeMenu}>
              <Image
                src="/image/sst_logo.svg"
                alt="SST Exames"
                width={150}
                height={40}
                className="h-8 md:h-10 w-auto aspect-[150/40]"
                priority
              />
            </Link>
          </div>

          {/* Navigation Links - Desktop */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={handleComoFunciona}
              className="text-gray-700 hover:text-green-600 transition-colors text-left"
            >
              Como Funciona
            </button>
            <Link href="/fale-conosco" className="text-gray-700 hover:text-green-600 transition-colors">
              Fale Conosco
            </Link>
            <Link href="/faq" className="text-gray-700 hover:text-green-600 transition-colors">
              Dúvidas frequentes
            </Link>
            <Link href="/clinicas" className="text-gray-700 hover:text-green-600 transition-colors">
              Lista de Parceiros
            </Link>
          </div>

          {/* CTA Buttons - Desktop */}
          {!shouldHideButtons && (
            <div className="hidden md:flex items-stretch space-x-4">
              <Link
                href="/planos"
                className="bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 text-center leading-tight flex flex-col justify-center"
              >
                <span className="block">Contratar como</span>
                <span className="block">Assessoria</span>
              </Link>
              {!pathname.startsWith('/exames') && (
                <button
                  onClick={handleAgendarExames}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 flex items-center justify-center"
                >
                  Agendar Exames
                </button>
              )}
            </div>
          )}

          {/* Mobile: CTA Buttons + Menu Button */}
          <div className="md:hidden flex items-center space-x-1 flex-shrink-0">
            {/* CTA Buttons - Mobile (priorizados) */}
            {!shouldHideButtons && (
              <div className="flex items-stretch space-x-1">
                <Link
                  href="/planos"
                  className="bg-green-600 hover:bg-green-500 text-white px-2 py-1.5 rounded-lg font-semibold text-xs shadow-md transition-all whitespace-nowrap text-center leading-tight flex flex-col justify-center"
                  onClick={closeMenu}
                >
                  <span className="block">Contratar como</span>
                  <span className="block">Assessoria</span>
                </Link>
                {!pathname.startsWith('/exames') && (
                  <button
                    onClick={handleAgendarExames}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1.5 rounded-lg font-semibold text-xs shadow-md transition-all whitespace-nowrap flex items-center justify-center"
                  >
                    Agendar
                  </button>
                )}
              </div>
            )}
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="text-gray-700 hover:text-green-600 p-2 flex-shrink-0"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleComoFunciona}
                className="text-gray-700 hover:text-green-600 transition-colors py-2 text-left"
              >
                Como Funciona
              </button>
              <Link
                href="/fale-conosco"
                className="text-gray-700 hover:text-green-600 transition-colors py-2"
                onClick={closeMenu}
              >
                Fale Conosco
              </Link>
              <Link
                href="/faq"
                className="text-gray-700 hover:text-green-600 transition-colors py-2"
                onClick={closeMenu}
              >
                Dúvidas frequentes
              </Link>
              <Link
                href="/clinicas"
                className="text-gray-700 hover:text-green-600 transition-colors py-2"
                onClick={closeMenu}
              >
                Lista de Parceiros
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
